import type {PageChangeOperation, PageChangeSetStatusItem} from '../chat-status';

/**
 * The changes touching participant input the user ticked, shared by the page and the chat card so a tick in one
 * shows in the other (D4.2). A tick belongs to the revision of the set it was given on: any change to the set
 * voids it, so the user ticks again what they review again.
 */
export class ChangeConfirmations {
    private readonly ticked = new Map<string, Set<string>>();
    private readonly listeners = new Set<() => void>();

    isTicked(set: PageChangeSetStatusItem, operationId: string): boolean {
        return this.ticked.get(keyOf(set))?.has(operationId) ?? false;
    }

    set(set: PageChangeSetStatusItem, operationId: string, ticked: boolean): void {
        const key = keyOf(set);
        const ids = this.ticked.get(key) ?? new Set<string>();
        if (ticked) {
            ids.add(operationId);
        } else {
            ids.delete(operationId);
        }
        this.ticked.set(key, ids);
        this.listeners.forEach(listener => listener());
    }

    /**
     * The ticked changes that still need the tick, sent with an approval.
     */
    confirmed(set: PageChangeSetStatusItem): string[] {
        return set.operations.filter(operation => needsTick(operation) && this.isTicked(set, operation.id))
            .map(operation => operation.id);
    }

    /**
     * How many changes an apply of the set would write: the ones waiting that still hold, ticked where they need it.
     */
    applicableCount(set: PageChangeSetStatusItem): number {
        return set.operations.filter(operation => (
            operation.status === 'pending'
            && operation.blocked === null
            && (!operation.needsConfirmation || this.isTicked(set, operation.id))
        )).length;
    }

    onChange(listener: () => void): void {
        this.listeners.add(listener);
    }
}

export const needsTick = (operation: PageChangeOperation): boolean => (
    operation.status === 'pending' && operation.blocked === null && operation.needsConfirmation
);

const keyOf = (set: PageChangeSetStatusItem): string => `${set.id}:${set.revision}`;
